import type { Analysis, Dimensions, DimensionName } from '@fishymeter/types';
import { ContentPreview } from '@/components/content-preview';
import { DimensionCard } from '@/components/dimension-card';
import { FeedbackButton } from '@/components/feedback-button';
import { FishBadge } from '@/components/fish-badge';
import { ScoreGauge } from '@/components/score-gauge';

const DIMENSION_LABELS: Record<DimensionName, string> = {
  accuracy: 'Accuracy',
  consistency: 'Internal Consistency',
  attribution: 'Source Attribution',
  bias: 'Bias',
  originality: 'Originality',
  transparency: 'Transparency',
};

export interface AnalysisResultProps {
  analysis: Analysis;
  /** Optional permalink action shown above the verdict (e.g. share button). */
  actions?: React.ReactNode;
}

export function AnalysisResult({ analysis, actions }: AnalysisResultProps) {
  if (!analysis.dimensions || analysis.overall_score === undefined || !analysis.grade) {
    return null;
  }
  const dimensions = analysis.dimensions as Dimensions;

  return (
    <section className="space-y-8">
      <ContentPreview analysis={analysis} />
      <div className="rounded-lg border border-border bg-card p-8 text-center">
        <FishBadge grade={analysis.grade} size="lg" />
        <p className="mt-4 text-xl font-medium leading-relaxed text-foreground">
          {analysis.verdict}
        </p>
        <div className="mt-6 flex justify-center">
          <ScoreGauge score={analysis.overall_score} grade={analysis.grade} size="lg" />
        </div>
        {analysis.title && <p className="mt-4 text-lg font-semibold">{analysis.title}</p>}
        <p className="mt-1 text-xs text-muted-foreground">
          {analysis.username && (
            <>
              metered by <span className="font-medium text-foreground">@{analysis.username}</span>
              {' · '}
            </>
          )}
          {analysis.word_count !== undefined && `${analysis.word_count} words`}
          {analysis.created_at && ` · ${new Date(analysis.created_at).toLocaleDateString()}`}
        </p>
        {actions && <div className="mt-4 flex justify-center">{actions}</div>}
      </div>

      <div>
        <h2 className="text-2xl font-bold">Six dimensions</h2>
        <div className="mt-4 grid gap-4 sm:grid-cols-2">
          {(Object.keys(DIMENSION_LABELS) as DimensionName[]).map((key) => (
            <DimensionCard
              key={key}
              name={DIMENSION_LABELS[key]}
              score={dimensions[key].score}
              explanation={dimensions[key].explanation}
              evidence={dimensions[key].evidence}
            />
          ))}
        </div>
        <div className="mt-4 text-center">
          <FeedbackButton
            variant="link"
            analysisId={analysis.analysis_id}
            label="Feedback on this result"
          />
        </div>
      </div>
    </section>
  );
}
